import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import { Alert, Autocomplete, FormControlLabel, Stack, Switch, Typography, Divider, MenuItem, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {StyledModal, StyledModalBody, StyledModalFooter, StyledModalHeader, StyledModalInnerWrapper } from '../GraphModal/GraphModal';
import { CloudinaryUpload } from '../../CloudinaryUpload';
import { getEdgeCategoryOptions } from '../../edgeCategories';
import { normalizeGraphType } from '../../graphTypes';
import { getOriginalGraphEdges } from '../../graphEdges';

const ADD_PERSON_EDGE_MUTATION = gql`
  mutation AddPersonEdge($name: String!, $imageURL: String, $birthDate: Date, $deathDate: Date, $person2: ID!, $notes: String!, $dateMet: Date!, $category: String, $graphName: String) {
  addPersonEdge(name: $name, imageURL: $imageURL, birthDate: $birthDate, deathDate: $deathDate, person2: $person2, notes: $notes, dateMet: $dateMet, category: $category, graphName: $graphName) {
    _id   
  }
}
`;

const ADD_PERSON_MUTATION = gql`
  mutation AddPerson($name: String!, $imageURL: String, $birthDate: Date, $deathDate: Date, $graphName: String) {
    addPerson(name: $name, imageURL: $imageURL, birthDate: $birthDate, deathDate: $deathDate, graphName: $graphName) {
      _id
    }
  }
`;

const ADD_PERSON_BETWEEN_EDGE_MUTATION = gql`
  mutation AddPersonBetweenEdge(
    $name: String!
    $imageURL: String
    $birthDate: Date
    $deathDate: Date
    $replaceEdgeId: ID!
    $firstNotes: String!
    $firstDateMet: Date!
    $firstCategory: String
    $secondNotes: String!
    $secondDateMet: Date!
    $secondCategory: String
    $graphName: String
  ) {
    addPersonBetweenEdge(
      name: $name
      imageURL: $imageURL
      birthDate: $birthDate
      deathDate: $deathDate
      replaceEdgeId: $replaceEdgeId
      firstNotes: $firstNotes
      firstDateMet: $firstDateMet
      firstCategory: $firstCategory
      secondNotes: $secondNotes
      secondDateMet: $secondDateMet
      secondCategory: $secondCategory
      graphName: $graphName
    ) {
      _id
    }
  }
`;


const imageStyle = {
  width: 'clamp(64px, 18vw, 100px)',
  height: 'clamp(64px, 18vw, 100px)',
  margin: '0 auto',
  display: 'block',
};

const dashedLineStyle = {
  border: '1px dashed #000',
  width: '100%',
  height: '0px',
};

export const CreatePersonModal = ({
  people,
  edges = [],
  isModalOpen,
  setIsModalOpen,
  refetchGraph,
  graphName,
  graphType = 'social',
}) => {
  const [addPersonEdge, { loading: isSaving, error: saveError }] = useMutation(ADD_PERSON_EDGE_MUTATION);
  const [addPerson, { loading: isSavingFirstPerson, error: firstPersonError }] = useMutation(ADD_PERSON_MUTATION);
  const [addPersonBetweenEdge, { loading: isSavingBetweenEdge, error: betweenEdgeError }] = useMutation(ADD_PERSON_BETWEEN_EDGE_MUTATION);
  const theme = useTheme()
  const [step, setStep] = useState(0);

  const [editedNodeName, setEditedNodeName] = useState('');
  const [editedNodeImage, setEditedNodeImage] = useState('');
  const [editedBirthDate, setEditedBirthDate] = useState();
  const [editedDeathDate, setEditedDeathDate] = useState();

  const [editedEdgeNote, setEditedEdgeNote] = useState('');
  const [editedEdgeDateMet, setEditedEdgeDateMet] = useState();
  const [editedEdgeCategory, setEditedEdgeCategory] = useState('friendship');

  const [person2, setPerson2] = useState(null);
  const [shouldReplaceEdge, setShouldReplaceEdge] = useState(false);
  const [replaceEdge, setReplaceEdge] = useState(null);
  const [firstEdgeNote, setFirstEdgeNote] = useState('');
  const [firstEdgeDateMet, setFirstEdgeDateMet] = useState();
  const [firstEdgeCategory, setFirstEdgeCategory] = useState('friendship');
  const [secondEdgeNote, setSecondEdgeNote] = useState('');
  const [secondEdgeDateMet, setSecondEdgeDateMet] = useState();
  const [secondEdgeCategory, setSecondEdgeCategory] = useState('friendship');

  const hasLoadedPeople = Array.isArray(people);
  const isFirstPerson = hasLoadedPeople && people.length === 0;
  const isSavingCurrentStep = isSaving || isSavingFirstPerson || isSavingBetweenEdge;
  const currentSaveError = firstPersonError || saveError || betweenEdgeError;
  const normalizedGraphType = normalizeGraphType(graphType);
  const categoryOptions = useMemo(() => getEdgeCategoryOptions(normalizedGraphType), [normalizedGraphType]);
  const defaultCategory = categoryOptions[0]?.value || 'friendship';

  useEffect(() => {
    setEditedEdgeCategory((current) => categoryOptions.some((option) => option.value === current) ? current : defaultCategory);
    setFirstEdgeCategory((current) => categoryOptions.some((option) => option.value === current) ? current : defaultCategory);
    setSecondEdgeCategory((current) => categoryOptions.some((option) => option.value === current) ? current : defaultCategory);
  }, [categoryOptions, defaultCategory]);
  const idToPerson = useMemo(() => (
    (people || []).reduce((acc, person) => {
      acc[person._id] = person;
      return acc;
    }, {})
  ), [people]);
  const replaceableEdges = useMemo(() => (
    getOriginalGraphEdges(edges || []).filter((edge) => idToPerson[edge.person1] && idToPerson[edge.person2])
  ), [edges, idToPerson]);
  const selectedReplacePerson1 = replaceEdge ? idToPerson[replaceEdge.person1] : null;
  const selectedReplacePerson2 = replaceEdge ? idToPerson[replaceEdge.person2] : null;
  const getEdgeLabel = (edge) => {
    if (!edge) return '';
    const firstPerson = idToPerson[edge.person1];
    const secondPerson = idToPerson[edge.person2];
    return `${firstPerson?.name || 'Person one'} + ${secondPerson?.name || 'Person two'}`;
  };
  const toMutationDate = (dateValue) => (dateValue?.toDate ? dateValue.toDate() : dateValue);

  const resetForm = () => {
    setStep(0);
    setEditedNodeName('');
    setEditedNodeImage('');
    setEditedBirthDate(null);
    setEditedDeathDate(null);
    setPerson2(null);
    setEditedEdgeDateMet(null);
    setEditedEdgeNote('');
    setEditedEdgeCategory(defaultCategory);
    setShouldReplaceEdge(false);
    setReplaceEdge(null);
    setFirstEdgeDateMet(null);
    setFirstEdgeNote('');
    setFirstEdgeCategory(defaultCategory);
    setSecondEdgeDateMet(null);
    setSecondEdgeNote('');
    setSecondEdgeCategory(defaultCategory);
  };

  const handleAddFirstPerson = async () => {
    if (!editedNodeName) return;
    await addPerson({
      variables: {
        name: editedNodeName,
        imageURL: editedNodeImage,
        birthDate: toMutationDate(editedBirthDate),
        deathDate: toMutationDate(editedDeathDate),
        graphName,
      },
    });
    await refetchGraph();
    resetForm();
    setIsModalOpen(false);
  };

  const handleAddPerson = async () => {
    if (shouldReplaceEdge) {
      if (!editedNodeName || !replaceEdge || !firstEdgeNote || !firstEdgeDateMet || !secondEdgeNote || !secondEdgeDateMet) return;
      await addPersonBetweenEdge({
        variables: {
          name: editedNodeName,
          imageURL: editedNodeImage,
          birthDate: toMutationDate(editedBirthDate),
          deathDate: toMutationDate(editedDeathDate),
          replaceEdgeId: replaceEdge._id,
          firstNotes: firstEdgeNote,
          firstDateMet: toMutationDate(firstEdgeDateMet),
          firstCategory: firstEdgeCategory,
          secondNotes: secondEdgeNote,
          secondDateMet: toMutationDate(secondEdgeDateMet),
          secondCategory: secondEdgeCategory,
          graphName,
        },
      });
      await refetchGraph();
      resetForm();
      setIsModalOpen(false);
      return;
    }

    if (editedNodeName  && person2 && editedEdgeNote && editedEdgeDateMet) {
      const dateMet = toMutationDate(editedEdgeDateMet);
      await addPersonEdge({
        variables: {
          name: editedNodeName,
          imageURL: editedNodeImage,
          birthDate: toMutationDate(editedBirthDate),
          deathDate: toMutationDate(editedDeathDate),
          person2: person2._id,
          notes: editedEdgeNote,
          dateMet,
          category: editedEdgeCategory,
          graphName,
        },
      });
      await refetchGraph();
      resetForm();
      setIsModalOpen(false);
    }
  };

  const handleClose = () => {
    resetForm();
    setIsModalOpen(false);
  };

  const handlePrevious = () => {
    setStep((x) => x - 1);
  };

  const handleNext = () => {
    if (step === 0) {
      if (isFirstPerson) {
        handleAddFirstPerson();
        return;
      }
      setStep(1);
    } else {
      handleAddPerson();
    }
  };

  if (!isModalOpen) return null;

  return (
    <Modal open={isModalOpen} onClose={handleClose}>
      <StyledModal className="graph-edit-modal">
        {step === 0 && (
          <StyledModalInnerWrapper>
            <StyledModalHeader className="graph-modal-header--compact">
              <Avatar
                alt="avatar"
                src={editedNodeImage}
                sx={{
                  width: { xs: 96, sm: 150 },
                  height: { xs: 96, sm: 150 },
                  mt: { xs: 0, sm: 1 },
                }}
              />
              <Typography variant="h5">
                {editedNodeName || 'Jane Doe'}
              </Typography>
            </StyledModalHeader>

            <StyledModalBody>
              <TextField
                type="text"
                value={editedNodeName}
                onChange={(e) => setEditedNodeName(e.target.value)}
                label="Name"
                width="300px"
                sx={{ width: '100%' }}
              />
              <TextField
                type="text"
                value={editedNodeImage}
                onChange={(e) => setEditedNodeImage(e.target.value)}
                label="Image URL"
                sx={{ width: '100%' }}
              />
              <CloudinaryUpload
                imageUrl={editedNodeImage}
                setImageUrl={setEditedNodeImage}
              />
              {normalizedGraphType === 'family' && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
                    <DatePicker sx={{ width: '100%' }} label="Born" value={editedBirthDate || null} onChange={(value) => setEditedBirthDate(value)} />
                    <DatePicker sx={{ width: '100%' }} label="Died" value={editedDeathDate || null} onChange={(value) => setEditedDeathDate(value)} />
                  </Stack>
                </LocalizationProvider>
              )}
              {!hasLoadedPeople ? (
                <Alert severity="info">
                  Checking this map before adding someone.
                </Alert>
              ) : isFirstPerson ? (
                <Alert severity="info">
                  This map needs a first person before you can add relationship stories.
                </Alert>
              ) : (
                <>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={shouldReplaceEdge}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setShouldReplaceEdge(checked);
                          setPerson2(null);
                          setReplaceEdge(null);
                        }}
                      />
                    )}
                    label="Insert between two people by replacing an existing map line"
                  />
                  {shouldReplaceEdge ? (
                    <Autocomplete
                      options={replaceableEdges}
                      getOptionLabel={getEdgeLabel}
                      value={replaceEdge}
                      onChange={(e, value) => setReplaceEdge(value)}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      renderInput={(params) => (
                        <TextField {...params} label="Which connection should this person sit between?" sx={{ width: '100%' }} />
                      )}
                    />
                  ) : (
                    <Autocomplete
                      id="combo-box-demo"
                      options={people || []}
                      getOptionLabel={(person) => person.name}
                      value={person2}
                      onChange={(e, value) => setPerson2(value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Who do you know?" sx={{ width: '100%' }} />
                      )}
                    />
                  )}
                  {shouldReplaceEdge && !replaceableEdges.length && (
                    <Alert severity="info">
                      Add at least one map connection before inserting someone between two people.
                    </Alert>
                  )}
                </>
              )}
              {currentSaveError && <Alert severity="error">{currentSaveError.message}</Alert>}
            </StyledModalBody>
           <StyledModalFooter>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={!hasLoadedPeople || !editedNodeName || (!isFirstPerson && (shouldReplaceEdge ? !replaceEdge : !person2)) || isSavingCurrentStep}
              >
                {isFirstPerson ? 'Create First Person' : 'Next'}
              </Button>
              <Button variant="outlined" color="primary" onClick={handleClose}> Cancel </Button>
            </StyledModalFooter>
          </StyledModalInnerWrapper>
        )}
        {step === 1 && (
          <StyledModalInnerWrapper>
            <StyledModalHeader>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: { xs: 0.75, sm: 2 },
                  marginTop: { xs: 0, sm: '20px' },
                  width: '95%',
                  height: { xs: 'auto', sm: '230px' },
                }}
              >
                {shouldReplaceEdge && selectedReplacePerson1 && (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        alignItems: 'center',
                        minWidth: 0,
                      }}
                    >
                      <Avatar
                        alt={selectedReplacePerson1.name}
                        src={selectedReplacePerson1.imageURL || `https://robohash.org/${selectedReplacePerson1.name}`}
                        sx={imageStyle}
                      />
                      <Typography variant="h5" align="center" gutterBottom sx={{ fontSize: { xs: 14, sm: 20 }, overflowWrap: 'anywhere' }}>
                        {selectedReplacePerson1.name}
                      </Typography>
                    </Box>
                    <Box
                      flexItem
                      sx={{
                        display: 'flex',
                        width: { xs: '10%', sm: '28%' },
                        flexDirection: 'column',
                        alignItems: 'center',
                        marginTop: { xs: 0, sm: '50px' },
                      }}
                    >
                      <Divider style={dashedLineStyle} flexItem />
                    </Box>
                  </>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'center',
                    minWidth: 0,
                  }}
                >
                  <Avatar
                    alt={editedNodeName}
                    src={editedNodeImage || `https://robohash.org/${editedNodeName}`}
                    sx={imageStyle}
                  />
                  <Typography variant="h5" align="center" gutterBottom sx={{ fontSize: { xs: shouldReplaceEdge ? 14 : 18, sm: 24 }, overflowWrap: 'anywhere' }}>
                    {editedNodeName}
                  </Typography>
                </Box>
                <Box
                  flexItem
                  sx={{
                    display: 'flex',
                    width: { xs: shouldReplaceEdge ? '10%' : '28%', sm: shouldReplaceEdge ? '28%' : '80%' },
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginTop: { xs: 0, sm: '50px' },
                  }}
                >
                  <Divider style={dashedLineStyle} flexItem />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'center',
                    minWidth: 0,
                  }}
                >
                  <Avatar
                    alt={shouldReplaceEdge ? selectedReplacePerson2?.name : person2?.name}
                    src={shouldReplaceEdge
                      ? selectedReplacePerson2?.imageURL || `https://robohash.org/${selectedReplacePerson2?.name}`
                      : person2?.imageURL || `https://robohash.org/${person2?.name}`}
                    sx={imageStyle}
                  />
                  <Typography variant="h5" align="center" sx={{ fontSize: { xs: shouldReplaceEdge ? 14 : 18, sm: 24 }, overflowWrap: 'anywhere' }}>
                    {shouldReplaceEdge ? selectedReplacePerson2?.name : person2?.name}
                  </Typography>
                </Box>
              </Box>
            </StyledModalHeader>

            <StyledModalBody>
              {shouldReplaceEdge ? (
                <>
                  <Alert severity="info">
                    This will replace {getEdgeLabel(replaceEdge)} with two map lines through {editedNodeName}.
                  </Alert>
                  <Stack spacing={2}>
                    <Typography variant="h6">
                      How {editedNodeName} met {selectedReplacePerson1?.name}
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker sx={{ width: '100%' }} label="When did they meet?" onChange={(e) => setFirstEdgeDateMet(e)} />
                    </LocalizationProvider>
                    <TextField
                      select
                      label="Meeting category"
                      value={firstEdgeCategory}
                      onChange={(e) => setFirstEdgeCategory(e.target.value)}
                      sx={{ width: '100%' }}
                    >
                      {categoryOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      multiline
                      minRows={3}
                      value={firstEdgeNote}
                      onChange={(e) => setFirstEdgeNote(e.target.value)}
                      label={`How ${editedNodeName} met ${selectedReplacePerson1?.name || 'them'}`}
                      sx={{ width: '100%' }}
                    />
                  </Stack>
                  <Divider />
                  <Stack spacing={2}>
                    <Typography variant="h6">
                      How {editedNodeName} met {selectedReplacePerson2?.name}
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker sx={{ width: '100%' }} label="When did they meet?" onChange={(e) => setSecondEdgeDateMet(e)} />
                    </LocalizationProvider>
                    <TextField
                      select
                      label="Meeting category"
                      value={secondEdgeCategory}
                      onChange={(e) => setSecondEdgeCategory(e.target.value)}
                      sx={{ width: '100%' }}
                    >
                      {categoryOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      multiline
                      minRows={3}
                      value={secondEdgeNote}
                      onChange={(e) => setSecondEdgeNote(e.target.value)}
                      label={`How ${editedNodeName} met ${selectedReplacePerson2?.name || 'them'}`}
                      sx={{ width: '100%' }}
                    />
                  </Stack>
                </>
              ) : (
                <>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker  sx={{ width: '100%' }} label="When Did You Meet?" onChange={(e) => setEditedEdgeDateMet(e)} />
                  </LocalizationProvider>
                  <TextField
                    select
                    label="Meeting category"
                    value={editedEdgeCategory}
                    onChange={(e) => setEditedEdgeCategory(e.target.value)}
                    sx={{ width: '100%' }}
                  >
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    type="text"
                    multiline
                    minRows={3}
                    value={editedEdgeNote}
                    onChange={(e) => setEditedEdgeNote(e.target.value)}
                    label="How We Met"
                     sx={{ width: '100%' }}
                  />
                </>
              )}
              
            </StyledModalBody>

            <StyledModalFooter sx={{ backgroundColor: theme.palette.secondary.main }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isSavingCurrentStep || (shouldReplaceEdge
                  ? !firstEdgeDateMet || !firstEdgeNote || !secondEdgeDateMet || !secondEdgeNote
                  : !editedEdgeDateMet || !editedEdgeNote)}
              >
                Save
              </Button>
              <Button variant="outlined" onClick={handlePrevious} disabled={isSavingCurrentStep}>Previous</Button>
            </StyledModalFooter>
          </StyledModalInnerWrapper>
        )}
      </StyledModal>
    </Modal>
  );
};
